import { useState, useRef } from "react";
import { getEndpoint, API_ENDPOINTS } from "../api/apiConfig";
import { setToken } from "../api/auth";
import { FiMail, FiLock, FiEyeOff, FiEye, FiAlertCircle } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import "../styles/login.css";

function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  
  // Track if user info fetch has already been initiated for current login to prevent duplicate calls
  const userFetchInitiated = useRef(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!username || !password) {
      setError("Please enter both username and password");
      return;
    }

    setLoading(true);
    // Reset fetch flag for new login attempt
    userFetchInitiated.current = false;
    
    try {
      const response = await fetch(getEndpoint("auth", "login"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: username,
          password: password,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          const userSiteId = data.siteId || data.site_id || data.siteid || (typeof data.site === "object" ? data.site.id || data.site.siteId || data.site.site_id || null : data.site) || null;
          setToken(data.token, userSiteId);
          
          // Fetch user information to get userType and siteId (only once per login)
          if (!userFetchInitiated.current) {
            userFetchInitiated.current = true;
            
            try {
              const userEndpoint = API_ENDPOINTS.auth.user(username);
              const userResponse = await fetch(userEndpoint, {
                method: "GET",
                headers: {
                  "Authorization": `Bearer ${data.token}`,
                  "Content-Type": "application/json",
                },
              });
              
              let userType = '';
              let siteId = null;
              let userData = null;
              if (userResponse.ok) {
                userData = await userResponse.json();
                // Check for different possible userType field names
                userType = userData.userType || userData.type || userData.user_type || userData.usertype || userData.role || userData.userRole || userData.roleName || userData.roles || '';
                // Check for siteId in user data
                siteId = userData.siteId || userData.site_id || userData.siteid || (typeof userData.site === "object" ? userData.site.id || userData.site.siteId || userData.site.site_id || null : userData.site) || null;
                const normalizedSiteId = siteId || userSiteId;
                if (normalizedSiteId) {
                  userData = { ...userData, siteId: normalizedSiteId };
                }
                console.log("User info fetched:", userData);
                console.log("Extracted userType:", userType, "siteId:", normalizedSiteId);
                
                // Update token with siteId if found
                if (normalizedSiteId && !userSiteId) {
                  setToken(data.token, normalizedSiteId);
                }
              } else {
                console.log("User fetch failed with status:", userResponse.status);
                const errorText = await userResponse.text();
                console.log("User fetch error:", errorText);
                
                // If 403, try without Authorization header (backend might not need it for this endpoint)
                if (userResponse.status === 403) {
                  console.log("Got 403, trying without Authorization header...");
                  const retryResponse = await fetch(userEndpoint, {
                    method: "GET",
                    headers: {
                      "Content-Type": "application/json",
                    },
                  });
                  
                  if (retryResponse.ok) {
                    userData = await retryResponse.json();
                    userType = userData.userType || userData.type || userData.user_type || userData.usertype || userData.role || userData.userRole || userData.roleName || userData.roles || '';
                    siteId = userData.siteId || userData.site_id || userData.siteid || (typeof userData.site === "object" ? userData.site.id || userData.site.siteId || userData.site.site_id || null : userData.site) || null;
                    const normalizedSiteId = siteId || userSiteId;
                    if (normalizedSiteId) {
                      userData = { ...userData, siteId: normalizedSiteId };
                    }
                    console.log("Retry successful! User info:", userData);
                    console.log("Extracted userType from retry:", userType, "siteId:", normalizedSiteId);
                    
                    // Update token with siteId if found
                    if (normalizedSiteId && !userSiteId) {
                      setToken(data.token, normalizedSiteId);
                    }
                  } else {
                    console.log("Retry also failed with status:", retryResponse.status);
                  }
                }
              }
              
              const authUser = userData ? { ...userData, siteId: userData.siteId || userData.site_id || userData.siteid || (typeof userData.site === "object" ? userData.site.id || userData.site.siteId || userData.site.site_id || null : userData.site) || userSiteId } : (userSiteId ? { siteId: userSiteId } : null);
              console.log("Calling onLogin with username:", username, "userType:", userType, "userData:", authUser);
              onLogin(data.message || username, userType, authUser);
              navigate("/");
            } catch (userError) {
              console.error("Error fetching user info:", userError);
              // Continue with login even if user fetch fails
              onLogin(data.message || username, '', null);
              navigate("/");
            }
          } else {
            // User fetch not initiated, just proceed with login
            onLogin(data.message || username, '', null);
            navigate("/");
          }
        }
      } else {
        const err = await response.json().catch(() => ({}));
        setError(err.message || "Invalid username or password");
      }
    } catch (e) {
      setError("Network error. Please try again.");
      console.error("Login error:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell">
      {/* NAVBAR */}
      <header className="login-navbar">
        <div className="logo-container">
          <div className="logo">
            <img src="/unnamed.png" alt="SKT Infracon Tracker" />
          </div>
          <span className="logo-text">SKT Infracon Tracker</span>
        </div>
      </header>

      {/* HERO SECTION */}
      <div className="login-hero">
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1>Welcome Back SKT INFRACON</h1>
          <p>Manage your construction site with ease</p>
        </div>
      </div>

      {/* LOGIN CARD */}
      <main className="login-container">
        <div className="login-card">
          <div className="card-header">
            <h2>Sign In to Your Account</h2>
            <p>Enter your credentials to access the dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="login-form">
            {/* Error Message */}
            {error && (
              <div className="error-alert">
                <FiAlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            {/* Username Field */}
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <div className="input-wrapper">
                <input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                />
                <FiMail className="input-icon" />
              </div>
            </div>

            {/* Password Field */}
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <FiLock className="input-icon" />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="btn-login"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          {/* Footer Info */}
          <div className="card-footer">
            <p>Secure login powered by SKT Infrastructure</p>
          </div>
        </div>

        {/* Right side info (for larger screens) */}
        <div className="login-info">
          <div className="info-card">
            <div className="info-icon">📊</div>
            <h3>Dashboard</h3>
            <p>View real-time statistics and insights</p>
          </div>
          <div className="info-card">
            <div className="info-icon">👥</div>
            <h3>Employee Tracking</h3>
            <p>Manage employees and attendance</p>
          </div>
          <div className="info-card">
            <div className="info-icon">📦</div>
            <h3>Inventory</h3>
            <p>Track purchases and sales</p>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Login;
